// src/features/orders/OrdersPage.tsx
import { useState } from 'react';
import { CheckCircle2, CircleOff, Clock3, ShoppingCart } from 'lucide-react';
import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { OrderTable } from './components';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { useOrders } from './hooks/useOrders';
import type { OrderStatus } from './types/order.types';
import './styles/orders.css';

type PaginationItem = number | 'ellipsis';

function getPaginationItems(currentPage: number, totalPages: number): PaginationItem[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, 'ellipsis', totalPages];
  if (currentPage >= totalPages - 3) return [1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, 'ellipsis', currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2, 'ellipsis', totalPages];
}

/**
 * Orders Foundation. Same layout pattern as CustomersPage/
 * ServicesPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Rows are loaded from the authenticated Super Admin Orders API through
 * useOrders. Legacy mock files remain inactive for rollback only.
 */
export function OrdersPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OrderStatus | undefined>();
  const { data, loading, error } = useOrders(page, status);

  const changeStatus = (value: string) => {
    setPage(1);
    setStatus(value ? value as OrderStatus : undefined);
  };

  return (
    <ControlPlanePage
      className="vania-orders-control-plane-page"
      title="مدیریت سفارش‌ها"
      description="مشاهده و پیگیری سفارش‌های ثبت‌شده در تمام تننت‌های پلتفرم."
      summary={data.total_count > 0 ? (
        <ControlPlaneMetricStrip
          items={[
            { label: 'کل سفارش‌ها', value: formatPersianNumber(data.total_count), icon: <ShoppingCart size={16} />, tone: 'primary' },
            { label: 'پرداخت‌شده', value: formatPersianNumber(data.items.filter((order) => order.status === 'paid').length), icon: <CheckCircle2 size={16} />, tone: 'success' },
            { label: 'در انتظار', value: formatPersianNumber(data.items.filter((order) => order.status === 'pending').length), icon: <Clock3 size={16} />, tone: 'warning' },
            { label: 'ردشده', value: formatPersianNumber(data.items.filter((order) => order.status === 'rejected').length), icon: <CircleOff size={16} />, tone: 'danger' },
          ]}
        />
      ) : undefined}
    >
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          {error && data.items.length === 0 ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          <div className="vania-orders-toolbar">
            <label className="vania-orders-status-filter" htmlFor="order-status">وضعیت
              <select id="order-status" value={status ?? ''} onChange={(event) => changeStatus(event.target.value)}>
                <option value="">همه</option>
                <option value="pending">در انتظار پرداخت</option>
                <option value="paid">پرداخت شده</option>
                <option value="rejected">رد شده</option>
                <option value="deleted">حذف شده</option>
                <option value="expired">منقضی شده</option>
                <option value="cancelled">لغو شده</option>
              </select>
            </label>
            <span className="vania-orders-total">{formatPersianNumber(data.total_count)} سفارش</span>
          </div>
          <div className="vania-orders-table-region">
            {error && data.items.length === 0 ? null : loading && data.items.length === 0 ? <OrdersTableSkeleton /> : !loading && data.items.length === 0 ? <EmptyState message="سفارشی یافت نشد." /> : <OrderTable orders={data.items} />}
          </div>
          <div className="vania-orders-pagination-bar">
            <label className="vania-orders-page-size"><span>تعداد در صفحه</span><select value="20" disabled aria-label="تعداد سفارش در هر صفحه"><option value="20">۲۰</option></select></label>
            <nav className="vania-orders-pagination" aria-label="صفحه‌بندی سفارش‌ها">
              <button type="button" className="vania-orders-pagination__control" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>‹</button>
              <div className="vania-orders-pagination__pages">{getPaginationItems(page, data.total_pages).map((item, index) => item === 'ellipsis' ? <span key={`ellipsis-${index}`} className="vania-orders-pagination__ellipsis" aria-hidden="true">…</span> : <button type="button" key={item} className={`vania-orders-pagination__page${item === page ? ' is-active' : ''}`} aria-current={item === page ? 'page' : undefined} onClick={() => setPage(item)}>{formatPersianNumber(item)}</button>)}</div>
              <button type="button" className="vania-orders-pagination__control" disabled={page >= data.total_pages} onClick={() => setPage((value) => value + 1)}>›</button>
            </nav>
          </div>
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
    </ControlPlanePage>
  );
}

function OrdersTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری سفارش‌ها">
      <div className="vania-orders-table-scroll" aria-hidden="true">
          <table className="vania-orders-table">
            <thead>
              <tr>
                {['شناسه سفارش', 'تننت', 'مشتری', 'سرویس', 'مبلغ', 'وضعیت', 'تاریخ ثبت', 'آخرین به‌روزرسانی'].map((heading) => <th key={heading}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 20 }, (_, rowIndex) => (
                <tr key={`order-skeleton-${rowIndex}`}>
                  {Array.from({ length: 8 }, (_, cellIndex) => (
                    <td key={`order-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </VaniaLoadingSystem>
  );
}
